/**
 * Stories API Service
 * Handles all API calls for the stories feature
 * Ported from Flutter: status_repository.dart
 */

import { apiUrl } from "@/utils/api";

// Get auth token from localStorage (consistent with chat.js and other services)
function getToken() {
  try {
    return localStorage.getItem("access_token");
  } catch (_) {
    return null;
  }
}

function getAuthHeaders() {
  const token = getToken();
  const headers = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  // Include country code if available (consistent with other services)
  if (
    typeof window !== "undefined" &&
    window.appRegion &&
    window.appRegion.phone_code
  ) {
    headers["x-country-code"] = window.appRegion.phone_code;
  }
  return headers;
}

// Helper to safely parse JSON
async function safeJson(res) {
  try {
    return await res.json();
  } catch (_) {
    return null;
  }
}

export const StoriesApi = {
  /**
   * Get all visible statuses grouped by user
   * Endpoint: GET /api/statuses
   */
  async getStatuses() {
    try {
      const res = await fetch(`${apiUrl}/statuses`, {
        method: "GET",
        headers: getAuthHeaders(),
      });

      if (!res.ok) {
        const error = await safeJson(res);
        throw new Error(error?.message || "Failed to fetch statuses");
      }

      const json = await safeJson(res);
      // API returns { data: [...] } or direct array
      return json?.data || json || [];
    } catch (e) {
      console.error("[StoriesApi] getStatuses error:", e);
      throw e;
    }
  },

  /**
   * Create a new status
   * Endpoint: POST /api/statuses
   */
  async createStatus({ type, mediaFile, textOverlay, description }) {
    try {
      const formData = new FormData();
      formData.append("type", type);

      if (textOverlay) {
        formData.append("text_overlay", textOverlay);
      }
      if (description) {
        formData.append("description", description);
      }
      if (mediaFile) {
        formData.append("media", mediaFile, mediaFile.name);
      }

      const headers = getAuthHeaders();
      delete headers["Content-Type"]; // Let browser set multipart boundary

      const res = await fetch(`${apiUrl}/statuses`, {
        method: "POST",
        headers,
        body: formData,
      });

      if (!res.ok) {
        const error = await safeJson(res);
        throw new Error(error?.message || "Failed to create status");
      }

      const json = await safeJson(res);
      return json?.message || "Status created successfully";
    } catch (e) {
      console.error("[StoriesApi] createStatus error:", e);
      throw e;
    }
  },

  /**
   * Delete a status
   * Endpoint: DELETE /api/statuses/{id}
   */
  async deleteStatus(statusId) {
    try {
      const res = await fetch(`${apiUrl}/statuses/${statusId}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });

      if (!res.ok) {
        const error = await safeJson(res);
        throw new Error(error?.message || "Failed to delete status");
      }

      return true;
    } catch (e) {
      console.error("[StoriesApi] deleteStatus error:", e);
      throw e;
    }
  },

  /**
   * Toggle reaction on a status
   * Endpoint: POST /api/statuses/{id}/react
   * Matches Flutter: AppEndpoint.toggleStatusReaction(statusId)
   * Returns: { userHasReacted: boolean, reactionCount: number }
   */
  async toggleReaction(statusId) {
    try {
      console.log("[StoriesApi] toggleReaction - statusId:", statusId);

      const res = await fetch(`${apiUrl}/statuses/${statusId}/react`, {
        method: "POST",
        headers: getAuthHeaders(),
      });

      console.log("[StoriesApi] toggleReaction - response status:", res.status);

      const json = await safeJson(res);
      console.log("[StoriesApi] toggleReaction - response body:", json);

      if (!res.ok) {
        console.error("[StoriesApi] toggleReaction - error response:", json);
        throw new Error(json?.message || "Failed to toggle reaction");
      }

      // Handle multiple response formats:
      // 1. { data: { user_has_reacted, reaction_count } }
      // 2. { user_has_reacted, reaction_count }
      // 3. { userHasReacted, reactionCount }
      const data = json?.data || json;
      const result = {
        userHasReacted: data?.user_has_reacted ?? data?.userHasReacted ?? false,
        reactionCount: data?.reaction_count ?? data?.reactionCount ?? 0,
      };

      console.log("[StoriesApi] toggleReaction - parsed result:", result);
      return result;
    } catch (e) {
      console.error("[StoriesApi] toggleReaction error:", e);
      throw e;
    }
  },

  /**
   * Mark status as viewed
   * Endpoint: POST /api/statuses/{statusId}/viewed
   * Silently fails - viewing shouldn't block UX
   */
  async markAsViewed(statusId) {
    try {
      await fetch(`${apiUrl}/statuses/${statusId}/viewed`, {
        method: "POST",
        headers: getAuthHeaders(),
      });
    } catch (e) {
      // Silently fail - viewing status shouldn't block the user experience
      console.warn("[StoriesApi] markAsViewed failed silently:", e);
    }
  },

  /**
   * Reply to a status
   * Endpoint: POST /api/statuses/{id}/reply
   * Matches Flutter: AppEndpoint.replyToStatus(statusId)
   * Body: { message: string }
   */
  async replyToStatus(statusId, message) {
    try {
      console.log(
        "[StoriesApi] replyToStatus - statusId:",
        statusId,
        "message:",
        message
      );

      const res = await fetch(`${apiUrl}/statuses/${statusId}/reply`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ message }),
      });

      console.log("[StoriesApi] replyToStatus - response status:", res.status);

      if (!res.ok) {
        const error = await safeJson(res);
        console.error("[StoriesApi] replyToStatus - error response:", error);
        throw new Error(error?.message || "Failed to send reply");
      }

      const json = await safeJson(res);
      console.log("[StoriesApi] replyToStatus - success response:", json);
      return json?.message || "Reply sent successfully";
    } catch (e) {
      console.error("[StoriesApi] replyToStatus error:", e);
      throw e;
    }
  },

  /**
   * Get current user's own statuses (for history page)
   * Endpoint: POST /api/statuses/my
   */
  async getMyStatuses() {
    try {
      const res = await fetch(`${apiUrl}/statuses/my-statuses`, {
        method: "GET",
        headers: getAuthHeaders(),
      });

      if (!res.ok) {
        const error = await safeJson(res);
        throw new Error(error?.message || "Failed to fetch my statuses");
      }

      const json = await safeJson(res);
      // API returns { status: "success", data: [...] }
      return json?.data || json || [];
    } catch (e) {
      console.error("[StoriesApi] getMyStatuses error:", e);
      throw e;
    }
  },

  /**
   * Get reactions for a status
   * Endpoint: GET /api/statuses/{id}/reactions
   */
  async getReactions(statusId) {
    try {
      const res = await fetch(`${apiUrl}/statuses/${statusId}/reactions`, {
        method: "GET",
        headers: getAuthHeaders(),
      });

      if (!res.ok) {
        const error = await safeJson(res);
        throw new Error(error?.message || "Failed to fetch reactions");
      }

      const json = await safeJson(res);
      return json?.data || json || [];
    } catch (e) {
      console.error("[StoriesApi] getReactions error:", e);
      throw e;
    }
  },
};

export default StoriesApi;
